import React, { useEffect, useReducer } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constant';
import { getDocumentRuntime, loginUrl, startTileUrl, tileEntryPath } from '../../backend/runtime';
import { restoredRouteState, routeStateKey } from '../../backend/navigation';
import { TILE_ROUTES } from '../../backend/tileRouting';

const VendorAuthGuard = ({ children, appTag }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [, refreshSession] = useReducer((value) => value + 1, 0);
  useEffect(() => {
    window.addEventListener('storage', refreshSession);
    return () => window.removeEventListener('storage', refreshSession);
  }, []);
  let runtime;
  let configurationError;
  try { runtime = getDocumentRuntime(); } catch (error) { configurationError = error.message; }
  const params = new URLSearchParams(location.search);
  const bindingChanged = runtime && ((params.has('backend') && params.get('backend') !== runtime.profile.id)
    || (params.has('workflow') && params.get('workflow') !== runtime.workflowId));
  const foreign = runtime && ((appTag || null) !== runtime.tile || bindingChanged);
  const detail = TILE_ROUTES.some(({ tile, path }) => tile === appTag && path.includes(':')
    && location.pathname.startsWith(path.split('/:')[0] + '/'));
  const invalidState = location.state && runtime && !runtime.ownState(location.state);
  const sessionActive = Boolean(runtime?.currentSession());
  useEffect(() => {
    if (!runtime) return;
    if (runtime.session && !sessionActive) {
      // An expired actor must not sign in inside a document bound to the old
      // actor/key. A fresh document also tears down sockets and pending UI.
      window.location.replace(loginUrl(runtime));
      return;
    }
    if (foreign) {
      window.location.replace(bindingChanged ? location.pathname + location.search
        : (appTag ? startTileUrl(appTag) : `${ROUTES.DASHBOARD}?backend=${runtime.profile.id}`));
      return;
    }
    if (invalidState) return;
    const handoff = new URLSearchParams(location.search).get('handoff');
    let restored = !location.state && restoredRouteState(runtime, location.pathname);
    if (handoff) {
      try {
        const value = JSON.parse(sessionStorage.getItem(`vendor-handoff:${handoff}`));
        sessionStorage.removeItem(`vendor-handoff:${handoff}`);
        if (value?.namespace === runtime.session?.namespace && value.tile === runtime.tile
            && value.pathname === location.pathname && value.expires > Date.now()) {
          restored = runtime.scopedState(value.state);
        }
      } catch { /* Unverified handoffs never carry IDs into this workflow. */ }
    }
    if (restored) {
      sessionStorage.setItem(routeStateKey(runtime, location.pathname), JSON.stringify(restored));
      const next = new URLSearchParams(location.search);
      next.delete('handoff');
      navigate(runtime.urlFor(location.pathname + '?' + next), { state: restored, replace: true });
    }
  }, [runtime, sessionActive, foreign, bindingChanged, invalidState, appTag, location.pathname, location.search, location.state, navigate]);
  if (configurationError) return <div role="alert">Backend configuration required: {configurationError}</div>;
  if (foreign) return <div role="status">Opening a new backend-bound workflow…</div>;
  if (!sessionActive && runtime.session) return <div role="status">This backend session changed. Opening a fresh sign-in…</div>;
  if (!sessionActive) return <Navigate to={loginUrl(runtime)} replace />;
  if (appTag && !runtime.session.allowedApps.includes(appTag)) return <div role="alert">This backend user cannot access this tile.</div>;
  if (invalidState || (detail && !location.state)) return (
    <div role="alert">
      This detail link has no record bound to the selected backend. Open it from this backend's list.
      <a href={runtime.urlFor(tileEntryPath(appTag))}>Open {appTag} list</a>
    </div>
  );
  return children;
};

export default VendorAuthGuard;
